import React, { useEffect } from "react";
import ArtifactInformation from "../templates/ArtifactInformation";
import Text from "@amzn/meridian/text";
import ArtifactDetailsPageSkeleton from "../templates/ArtifactDetailsPageSkeleton";
import { useParams } from "react-router-dom";
import { useBreadcrumbs } from "../../../features/breadcrumbs/hooks";
import Tag from "@amzn/meridian/tag";
import {
    GetArtifactDetailsByIdResponse,
    useGetArtifactDetailsByIdQuery,
} from "../../../endpoints/getArtifactDetailsById";
import { Error404 } from "../../error-404/Error404";
import LoaderBox from "../../../components/loader/LoaderBox";
import {
    formatDate,
    formatLongString,
    formatPhoneToolLink,
    formatString,
    formatStringWithUnit,
} from "../../../utils/formattingUtils";
import Link from "@amzn/meridian/link";
import MailsTo from "../templates/MailsTo";

export const ParcelDetails: React.FC = () => {
    const { parcelId } = useParams();
    const ARTIFACT_TYPE = "PARCEL";
    const { updateBreadcrumbs } = useBreadcrumbs();

    const { data, isLoading, isSuccess, isFetching } = useGetArtifactDetailsByIdQuery({
        artifactType: ARTIFACT_TYPE,
        artifactId: parcelId,
        businessUseCase: "SMART",
    });

    useEffect(() => {
        if (isSuccess && !isFetching && data) {
            const propertyId =
                data.artifactDetails.association.parcelAssociation.associatedPropertyArtifactId;

            updateBreadcrumbs([
                { text: "Home", href: "/" },
                {
                    text: data.artifactDetails.propertyNumber,
                    href: `/property-details/${propertyId}`,
                },
                { text: data.artifactDetails.parcelNumber, href: `/parcel-details/${parcelId}` },
            ]);
        }
    }, [data, isSuccess, isFetching]);

    return (
        <>
            {isLoading ? (
                <LoaderBox loadingText="Loading parcel..." />
            ) : isSuccess ? (
                <ArtifactDetailsPageSkeleton
                    detailsSectionHeight="260px"
                    parcelId={parcelId}
                    artifactDetails={getParcelDetailsPanel(data)}
                    tabs={getTabs(parcelId, ARTIFACT_TYPE, data)}
                    data={data}
                />
            ) : (
                <Error404 />
            )}
        </>
    );
};

const getTabs = (parcelId: string, parcelType: string, data: GetArtifactDetailsByIdResponse) => {
    return [
        {
            label: "Parcel information",
            value: "information",
            content: (
                <ArtifactInformation
                    statusAndOwnershipInformationPanel={getStatusAndOwnershipInformationPanel(data)}
                    financialInformationPanel={getFinancialInformationPanel(data)}
                    otherInformationPanel={getOtherInformationPanel(data)}
                    artifactId={parcelId}
                    artifactType={parcelType}
                />
            ),
        },
        {
            label: "Mails to the parcel",
            value: "artifacts-mails",
            content: <MailsTo artifactId={parcelId} associatedArtifactType={parcelType} />,
        },
    ];
};

const getParcelDetailsPanel = (data: GetArtifactDetailsByIdResponse) => {
    return [
        {
            columns: [
                {
                    label: "Associated Property Number",
                    value: (
                        <Link
                            href={
                                "/property-details/" +
                                data.artifactDetails.association.parcelAssociation
                                    .associatedPropertyArtifactId
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {data.artifactDetails.propertyNumber}
                        </Link>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Site code",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.siteCode)}
                        </Text>
                    ),
                },
                {
                    label: "Parcel type",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.parcelType)}
                        </Text>
                    ),
                },
                {
                    label: "Parcel sub type",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.parcelSubType)}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Parcel number",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.parcelNumber)}
                        </Text>
                    ),
                },

                {
                    label: "Gross building area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.artifactDetails.grossBuildingArea,
                                data.artifactDetails.grossBuildingAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Gross leasable area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.artifactDetails.grossLeasableArea,
                                data.artifactDetails.grossLeasableAreaUnit
                            )}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Site area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.artifactDetails.siteArea,
                                data.artifactDetails.siteAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Site usable area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.artifactDetails.siteUsableArea,
                                data.artifactDetails.siteUsableAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Year built",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.yearBuilt)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getStatusAndOwnershipInformationPanel = (data: GetArtifactDetailsByIdResponse) => {
    return [
        {
            columns: [
                {
                    label: "Property status",
                    value: (
                        <>
                            {"ACTIVE" === data.artifactDetails.status ? (
                                <Tag type="success">Active</Tag>
                            ) : (
                                <Tag type="error">In Active</Tag>
                            )}
                        </>
                    ),
                },

                {
                    label: "Ownership status",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.ownershipStatus)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Entity",
                    value: (
                        <Text color="primary" type="h100">
                            {formatLongString(data.artifactDetails.entity)}
                        </Text>
                    ),
                },

                {
                    label: "Sec. division/groups",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.secDivision)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Date opened",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.artifactDetails.dateOpened)}
                        </Text>
                    ),
                },

                {
                    label: "Date closed/Lease end",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.artifactDetails.dateClosed)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getFinancialInformationPanel = (data: GetArtifactDetailsByIdResponse) => {
    return [
        {
            columns: [
                {
                    label: "Sale date",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.artifactDetails.saleDate)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Lease rate",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.leaseRate)}
                        </Text>
                    ),
                },
                {
                    label: "Sale price",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.salePrice)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Construction costs",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.constructionCosts)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Currency",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.currency)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getOtherInformationPanel = (data: GetArtifactDetailsByIdResponse) => {
    return [
        {
            columns: [
                {
                    label: "Contacts",
                    value: formatLongString(data.artifactDetails.contacts),
                },
                {
                    label: "Assigned users",
                    value: formatPhoneToolLink(data.artifactDetails.assignedTo),
                },
                {
                    label: "Property revision date",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.artifactDetails.dateRevised)}
                        </Text>
                    ),
                },
                {
                    label: "Sub type",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.subType)}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Property other description",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.artifactDetails.otherDescription)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};
